'use strict';

/**
 * Agent viewer bundle entrypoint.
 *
 * Loaded by `public/viewer.html` after browserify packages this file
 * (with mediasoup-client + protoo-client) into `public/viewer.bundle.js`.
 *
 * Lifecycle:
 *   1. Parse roomId + token from `window.location.search`.
 *   2. Decode token (no verify — server already verified at WS upgrade).
 *      The `peerId` claim is what we use in the WS URL.
 *   3. Open protoo WebSocket to same origin.
 *   4. Build mediasoup-client Device, create recv transport, fetch
 *      existing producers, consume them.
 *   5. React to `newProducer` / `consumerClosed` notifications for the
 *      ongoing stream.
 *   6. Keep the user-visible `#status` chip in sync with state.
 *
 * The host (Electron / Android WebView / Chromium) is expected to
 * allow autoplay-with-sound. If autoplay is blocked, we surface a
 * "click to play" overlay (see `playOnGesture` below).
 */

const protooClient = require('protoo-client');
const mediasoupClient = require('mediasoup-client');

// ─────────────────────────────────────────────────────────────
// Tiny helpers
// ─────────────────────────────────────────────────────────────
const $ = (sel) => document.querySelector(sel);

function setStatus(text, level) {
  const el = $('#status');
  if (el) { el.textContent = text; el.className = level || ''; }
  const loader = $('#loader');
  if (loader) {
    if (level === 'ok') {
      loader.classList.add('hidden');
    } else {
      loader.classList.remove('hidden');
    }
  }
}

function decodeJwtPayload(token) {
  // Decode the middle segment of an HS256 JWT (no verify — server
  // verified at WS upgrade). base64url → JSON.
  const part = token.split('.')[1];
  if (!part) throw new Error('malformed token');
  const padded = part.replace(/-/g, '+').replace(/_/g, '/')
    + '==='.slice((part.length + 3) % 4);
  return JSON.parse(atob(padded));
}

function parseQuery() {
  const p = new URLSearchParams(window.location.search);
  const roomId = p.get('roomId');
  const token  = p.get('token');
  if (!roomId || !token) {
    throw new Error('missing roomId or token in URL');
  }
  const claims = decodeJwtPayload(token);
  if (!claims.peerId || claims.roomId !== roomId) {
    throw new Error('token claims mismatch');
  }
  return { roomId, token, peerId: claims.peerId };
}

function buildWsUrl({ roomId, peerId, token }) {
  // Same origin as the page; just swap http(s) → ws(s).
  const loc = window.location;
  const proto = loc.protocol === 'https:' ? 'wss:' : 'ws:';
  const qs = new URLSearchParams({ roomId, peerId, token });
  return `${proto}//${loc.host}/?${qs.toString()}`;
}

// ─────────────────────────────────────────────────────────────
// Main
// ─────────────────────────────────────────────────────────────
const now = () => new Date().toISOString();

async function main() {
  let params;
  try {
    params = parseQuery();
    console.log(`[VIEWER] ${now()} Parsed query:`, params);
  } catch (err) {
    console.error(`[VIEWER] ${now()} parseQuery failed:`, err);
    setStatus(`init error: ${err.message}`, 'err');
    return;
  }

  // ── Browser / WebRTC capability check ─────────────────────
  console.log(`[VIEWER] ${now()} UserAgent:`, navigator.userAgent);
  console.log(`[VIEWER] ${now()} RTCPeerConnection available:`, typeof RTCPeerConnection !== 'undefined');

  setStatus('connecting…');
  console.log(`[VIEWER] ${now()} Building WS URL`);

  const wsUrl = buildWsUrl(params);
  console.log(`[VIEWER] ${now()} WS URL:`, wsUrl);
  const transport = new protooClient.WebSocketTransport(wsUrl);
  const peer = new protooClient.Peer(transport);

  const state = {
    device: null,
    recvTransport: null,
    /** @type {Map<string, MediaStreamTrack>} */
    tracks: new Map(),
    /** @type {Map<string, { consumerId, kind }>} */
    consumersByProducer: new Map(),
  };

  // Separate streams: video tracks → muted <video> (reliable autoplay),
  // audio tracks → <audio autoplay> (unmuted, plays sound).
  const videoEl = $('#remote');
  const audioEl = $('#remote-audio');
  const videoStream = new MediaStream();
  const audioStream = new MediaStream();
  videoEl.srcObject = videoStream;
  audioEl.srcObject = audioStream;

  function attachTrack(track) {
    console.log(`[VIEWER] ${now()} attachTrack called. kind:`, track.kind, 'id:', track.id, 'readyState:', track.readyState, 'muted:', track.muted);
    track.onmute = () => console.log(`[VIEWER] ${now()} track onmute`);
    track.onunmute = () => console.log(`[VIEWER] ${now()} track onunmute`);
    if (track.kind === 'audio') {
      audioStream.addTrack(track);
      // Audio autoplay may be blocked by browser policy. Retry silently on
      // any user interaction — do NOT show the loader overlay for this.
      audioEl.play().then(() => console.log(`[VIEWER] ${now()} audioEl.play() success`)).catch((e) => {
        console.warn(`[VIEWER] ${now()} audioEl.play() blocked, will retry on interaction:`, e.message);
        const resume = () => { audioEl.play().catch(() => {}); };
        window.addEventListener('click',      resume, { once: true });
        window.addEventListener('touchstart', resume, { once: true });
      });
    } else {
      videoStream.addTrack(track);
      // Log actual track settings the browser assigned (resolution, fps).
      setTimeout(() => {
        try {
          const s = track.getSettings();
          console.log(`[VIEWER-QUALITY] ${now()} video track.getSettings():`, {
            width: s.width, height: s.height, frameRate: s.frameRate,
            deviceId: s.deviceId, displaySurface: s.displaySurface,
          });
        } catch (e) { console.warn('[VIEWER-QUALITY] getSettings failed:', e); }
      }, 300);
      // Video is muted so autoplay should always succeed. Show overlay only
      // if it truly fails (very rare).
      videoEl.play().then(() => {
        console.log(`[VIEWER] ${now()} videoEl.play() success`);
        setTimeout(() => {
          console.log(`[VIEWER-QUALITY] ${now()} videoEl rendered size: ${videoEl.videoWidth}x${videoEl.videoHeight}`);
        }, 500);
        startQualityLogs();
      }).catch((e) => { console.error(`[VIEWER] ${now()} videoEl.play() failed:`, e); playOnGesture(); });
    }
    console.log(`[VIEWER] ${now()} streams after add — video:`, videoStream.getTracks().map(t => ({ kind: t.kind, id: t.id })), 'audio:', audioStream.getTracks().map(t => ({ kind: t.kind, id: t.id })));
    _hasTrack = true;
    setStatus('live', 'ok');
  }

  function playOnGesture() {
    // Autoplay-with-sound is blocked → show a click overlay.
    setStatus('click to play', 'err');
    const handler = () => {
      videoEl.play().then(() => setStatus('live', 'ok')).catch(() => {});
      audioEl.play().catch(() => {});
      window.removeEventListener('click', handler);
      window.removeEventListener('touchstart', handler);
    };
    window.addEventListener('click', handler);
    window.addEventListener('touchstart', handler);
  }

  let _removed = false;
  let _hasTrack = false;
  let _statsInterval = null;
  // ICE restart state object (mirrors pattern from main VC codebase)
  const _recvIce = { timer: null, restarting: false };

  /**
   * Robust ICE restart with debounce + exponential backoff.
   * Mirrors the proven pattern from the main InstaVC codebase.
   */
  function restartIce(transport, ice, delay) {
    if (!transport) {
      console.warn(`[VIEWER] ${now()} restartIce: missing transport`);
      return;
    }
    clearTimeout(ice.timer);
    ice.timer = setTimeout(async () => {
      if (ice.restarting) return;
      ice.restarting = true;
      try {
        console.log(`[VIEWER] ${now()} restartIce: requesting new ICE params (delay=${delay}ms)`);
        const { iceParameters } = await peer.request('restartIce', { transportId: transport.id });
        await transport.restartIce({ iceParameters });
        ice.restarting = false;
        console.log(`[VIEWER] ${now()} restartIce: success`);
      } catch (err) {
        console.warn(`[VIEWER] ${now()} restartIce: failed`, err.message);
        ice.restarting = false;
        // Exponential backoff: retry with doubled delay (cap at 30s)
        const nextDelay = Math.min(delay * 2, 30000);
        ice.timer = setTimeout(() => restartIce(transport, ice, nextDelay), delay);
      }
    }, delay);
  }

  function startQualityLogs() {
    if (_statsInterval) return;
    _statsInterval = setInterval(async () => {
      if (!state.recvTransport) return;
      try {
        const stats = await state.recvTransport.getStats();
        stats.forEach((r) => {
          if (r.type === 'inbound-rtp' && r.kind === 'video') {
            console.log(`[VIEWER-QUALITY] ${now()} inbound-rtp video:`, {
              codec:          r.mimeType || '?',
              resolution:     `${r.frameWidth || '?'}x${r.frameHeight || '?'}`,
              fps:            r.framesPerSecond || '?',
              framesDecoded:  r.framesDecoded,
              framesDropped:  r.framesDropped,
              bytesReceived:  r.bytesReceived,
              packetsLost:    r.packetsLost,
              jitter:         r.jitter,
            });
          }
          if (r.type === 'inbound-rtp' && r.kind === 'audio') {
            console.log(`[VIEWER-QUALITY] ${now()} inbound-rtp audio:`, {
              codec:         r.mimeType || '?',
              bytesReceived: r.bytesReceived,
              packetsLost:   r.packetsLost,
              jitter:        r.jitter,
            });
          }
        });
        console.log(`[VIEWER-QUALITY] ${now()} videoEl live size: ${videoEl.videoWidth}x${videoEl.videoHeight}`);
      } catch (e) {
        console.warn(`[VIEWER-QUALITY] getStats error:`, e);
      }
    }, 5000);
    console.log(`[VIEWER-QUALITY] ${now()} periodic quality logging started (every 5s)`);
  }

  function stopQualityLogs() {
    if (_statsInterval) { clearInterval(_statsInterval); _statsInterval = null; }
  }

  function showRemoved() {
    _removed = true;
    videoEl.pause();
    videoEl.srcObject = null;
    audioEl.pause();
    audioEl.srcObject = null;
    const overlay = document.getElementById('removed-overlay');
    if (overlay) overlay.classList.add('show');
    setStatus('removed', 'err');
  }

  // ── protoo notifications from server ───────────────────────
  peer.on('notification', async (notif) => {
    try {
      if (notif.method === 'broadcastEnded') {
        console.log(`[VIEWER] ${now()} broadcastEnded:`, notif.data.reason);
        stopQualityLogs();
        clearTimeout(_recvIce.timer);
        // Clear video and audio
        videoEl.pause();
        videoEl.srcObject = null;
        audioEl.pause();
        audioEl.srcObject = null;
        // Close the recv transport first so ICE keepalives stop immediately
        // and don't spam dead SFU ports with 'wrong authentication'.
        if (state.recvTransport) {
          try { state.recvTransport.close(); } catch (_) {}
          state.recvTransport = null;
        }
        setStatus('broadcast ended', '');
      } else if (notif.method === 'removed') {
        showRemoved();
        try { peer.close(); } catch (_) {}
      } else if (notif.method === 'newProducer') {
        await consumeProducer(notif.data.producerId, notif.data.kind);
      } else if (notif.method === 'consumerClosed') {
        const { consumerId } = notif.data;
        // Remove track from the correct stream.
        for (const [producerId, info] of state.consumersByProducer.entries()) {
          if (info.consumerId === consumerId) {
            const t = state.tracks.get(consumerId);
            if (t) {
              (t.kind === 'audio' ? audioStream : videoStream).removeTrack(t);
              state.tracks.delete(consumerId);
            }
            state.consumersByProducer.delete(producerId);
            break;
          }
        }
      }
    } catch (err) {
      console.error('[viewer] notification handler', err);
    }
  });

  peer.on('open',         () => { console.log(`[VIEWER] ${now()} peer open`); onPeerOpen().catch(onFatal); });
  peer.on('failed',       () => { console.log(`[VIEWER] ${now()} peer failed`); stopQualityLogs(); if (!_removed) setStatus('connection failed', 'err'); });
  peer.on('disconnected', () => { console.log(`[VIEWER] ${now()} peer disconnected`); if (!_removed) setStatus('disconnected', 'err'); });
  peer.on('close',        () => {
    console.log(`[VIEWER] ${now()} peer close`);
    stopQualityLogs();
    clearTimeout(_recvIce.timer);
    // Clear video and audio
    videoEl.pause();
    videoEl.srcObject = null;
    audioEl.pause();
    audioEl.srcObject = null;
    // Always close the recv transport so the ICE agent stops immediately.
    // Without this, the RTCPeerConnection keeps sending keepalives to the
    // now-dead SFU media port → STUN 'wrong authentication' spam.
    if (state.recvTransport) {
      try { state.recvTransport.close(); } catch (_) {}
      state.recvTransport = null;
    }
    if (!_removed) setStatus('disconnected', 'err');
  });

  // ── post-handshake ─────────────────────────────────────────
  async function onPeerOpen() {
    setStatus('handshaking…');

    // 1) Router caps + ICE servers (TURN config from env).
    console.log(`[VIEWER] ${now()} requesting getRouterRtpCapabilities + getIceServers`);
    const [routerRtpCapabilities, { iceServers }] = await Promise.all([
      peer.request('getRouterRtpCapabilities'),
      peer.request('getIceServers'),
    ]);
    console.log(`[VIEWER] ${now()} Got router caps and iceServers`);

    try {
      state.device = new mediasoupClient.Device();
    } catch (err) {
      console.error(`[VIEWER] ${now()} mediasoupClient.Device() failed — browser not supported:`, err.message, 'UA:', navigator.userAgent);
      setStatus(`browser not supported: ${err.message}`, 'err');
      throw err;
    }
    try {
      await state.device.load({ routerRtpCapabilities });
    } catch (err) {
      console.error(`[VIEWER] ${now()} device.load() failed:`, err.message);
      setStatus(`device load failed: ${err.message}`, 'err');
      throw err;
    }

    // 2) recv transport.
    console.log(`[VIEWER] ${now()} requesting createWebRtcTransport(recv)`);
    const transportInfo = await peer.request('createWebRtcTransport',
      { direction: 'recv' });
    console.log(`[VIEWER] ${now()} Got recv transport. id:`, transportInfo.id);
    state.recvTransport = state.device.createRecvTransport({
      id: transportInfo.id,
      iceParameters:  transportInfo.iceParameters,
      iceCandidates:  transportInfo.iceCandidates,
      dtlsParameters: transportInfo.dtlsParameters,
      iceServers,
    });

    state.recvTransport.on('connect', ({ dtlsParameters }, callback, errback) => {
      console.log(`[VIEWER] ${now()} recvTransport connect event`);
      peer.request('connectWebRtcTransport',
        { transportId: state.recvTransport.id, dtlsParameters })
        .then(() => { console.log(`[VIEWER] ${now()} connectWebRtcTransport success`); callback(); })
        .catch((e) => { console.error(`[VIEWER] ${now()} connectWebRtcTransport failed:`, e); errback(e); });
    });

    state.recvTransport.on('connectionstatechange', (s) => {
      console.log(`[VIEWER] ${now()} recvTransport state:`, s);
      switch (s) {
        case 'disconnected':
          if (!_hasTrack) setStatus('media disconnected', 'err');
          // Attempt ICE restart with 2s initial delay + exponential backoff.
          restartIce(state.recvTransport, _recvIce, 2000);
          break;
        case 'failed':
          setStatus('media failed', 'err');
          // Also attempt restart on failed — may recover if it was transient.
          restartIce(state.recvTransport, _recvIce, 2000);
          break;
        case 'connected':
          // Clear any pending restart timer — we're back online.
          clearTimeout(_recvIce.timer);
          _recvIce.restarting = false;
          setStatus('live', 'ok');
          // Log which ICE candidate pair was selected so we can verify
          // whether TURN relay is being used (avoids NAT UDP timeout drops).
          try {
            const pc = state.recvTransport._handler._pc;
            if (pc && pc.getStats) {
              pc.getStats().then((stats) => {
                stats.forEach((r) => {
                  if (r.type === 'candidate-pair' && r.state === 'succeeded' && r.nominated) {
                    const local  = stats.get(r.localCandidateId);
                    const remote = stats.get(r.remoteCandidateId);
                    console.log(`[VIEWER-ICE] ${now()} selected pair:`, {
                      localType:  local  ? local.candidateType  : '?',
                      remoteType: remote ? remote.candidateType : '?',
                      localAddr:  local  ? `${local.ip}:${local.port}`   : '?',
                      remoteAddr: remote ? `${remote.ip}:${remote.port}` : '?',
                      protocol:   local  ? local.protocol : '?',
                    });
                  }
                });
              }).catch(() => {});
            }
          } catch (_) {}
          break;
        default:
          // connecting, new, etc. — clear any pending restart timer
          clearTimeout(_recvIce.timer);
          break;
      }
    });

    // 3) Catch up on producers already in the room.
    console.log(`[VIEWER] ${now()} requesting getProducers`);
    const { producers } = await peer.request('getProducers');
    console.log(`[VIEWER] ${now()} getProducers returned`, producers.length, 'producers:', producers);
    for (const p of producers) {
      await consumeProducer(p.producerId, p.kind);
    }
    setStatus(producers.length ? 'live' : 'waiting for stream…',
      producers.length ? 'ok' : '');
  }

  async function consumeProducer(producerId, kind) {
    console.log(`[VIEWER] ${now()} consumeProducer called. producerId:`, producerId, 'kind:', kind);
    if (state.consumersByProducer.has(producerId)) { console.log(`[VIEWER] ${now()} already consuming`); return; }
    if (!state.recvTransport) { console.log(`[VIEWER] ${now()} recvTransport not ready`); return; }

    console.log(`[VIEWER] ${now()} requesting consume. producerId:`, producerId);
    const info = await peer.request('consume', {
      producerId,
      rtpCapabilities: state.device.rtpCapabilities,
      transportId:     state.recvTransport.id,
    });

    console.log(`[VIEWER] ${now()} consume response:`, info);
    if (info.rtpParameters && info.rtpParameters.codecs && info.rtpParameters.codecs.length) {
      const codec = info.rtpParameters.codecs[0];
      console.log(`[VIEWER-QUALITY] ${now()} codec negotiated:`, {
        mimeType:    codec.mimeType,
        clockRate:   codec.clockRate,
        payloadType: codec.payloadType,
        parameters:  codec.parameters,
      });
    }
    const consumer = await state.recvTransport.consume({
      id:            info.id,
      producerId:    info.producerId,
      kind:          info.kind,
      rtpParameters: info.rtpParameters,
    });
    console.log(`[VIEWER] ${now()} local consumer created. consumerId:`, consumer.id, 'track:', consumer.track ? { id: consumer.track.id, kind: consumer.track.kind, muted: consumer.track.muted, readyState: consumer.track.readyState } : null);

    consumer.on('transportclose', () => console.log(`[VIEWER] ${now()} consumer transportclose`));
    consumer.on('producerclose', () => console.log(`[VIEWER] ${now()} consumer producerclose`));
    consumer.on('producerpause', () => console.log(`[VIEWER] ${now()} consumer producerpause`));
    consumer.on('producerresume', () => console.log(`[VIEWER] ${now()} consumer producerresume`));

    state.tracks.set(consumer.id, consumer.track);
    state.consumersByProducer.set(producerId, { consumerId: consumer.id, kind });

    // Request the highest simulcast layer only when simulcast is enabled.
    const sfuConfig = (typeof window !== 'undefined' && (window).__SFU_CONFIG__) || {};
    if (sfuConfig.enableSimulcast) {
      await peer.request('setPreferredLayers', {
        consumerId: consumer.id,
        spatialLayer: 2,
        temporalLayer: 2,
      }).catch((err) => {
        console.log(`[VIEWER] ${now()} setPreferredLayers failed (non-fatal):`, err);
      });
    }

    attachTrack(consumer.track);
    setStatus('live', 'ok');
    console.log(`[VIEWER] ${now()} consuming complete`, { producerId, kind, consumerId: consumer.id });
  }

  function onFatal(err) {
    console.error(`[VIEWER] ${now()} fatal`, err);
    setStatus(`error: ${err.message}`, 'err');
    try { peer.close(); } catch (_) { /* */ }
  }

  // Clean exit when the parent (webview/agent) closes us.
  window.addEventListener('beforeunload', () => {
    try { peer.close(); } catch (_) { /* */ }
  });
}

main();
